/* PAGINA 8 · DASHBOARD
 *
 * È la prima cosa che si vede, quindi risponde a "va tutto bene?" PRIMA che a
 * "quanto ho guadagnato".
 *
 * ## L'ordine dei blocchi, e perché è cambiato
 *
 * Prima era: tessere, patrimonio, patrimonio nel tempo (non disponibile), «Da
 * guardare adesso», e solo in fondo i bot. Su dodici bot — uno in marcia con
 * denaro vero, nove spenti a mano e configurati con denaro vero — il risultato
 * era una parete di nove schede identiche («fermo, ma configurato con denaro
 * reale») che occupava tutta la schermata e spingeva in fondo l'unica riga che
 * stesse davvero facendo qualcosa. La tessera in alto diceva «9 bot chiedono
 * attenzione»: nessuno dei nove chiedeva niente.
 *
 * Ora l'ordine è: tessere, CHI STA LAVORANDO ADESSO coi suoi numeri veri, poi
 * le cose azionabili (se ce ne sono), poi il capitale, poi tutto il resto. Un
 * bot spento il mese scorso non ha niente da dire; quello che sta muovendo
 * denaro adesso ha tutto. L'ordine dentro ogni elenco viene da
 * `ordinaPerImportanza`, non più dall'id decrescente — cioè non più dalla data
 * di creazione, che con l'importanza non c'entra.
 *
 * ## Le quattro letture
 *
 * Non esiste un endpoint di riepilogo. Il riepilogo nasce da quattro chiamate,
 * di cui due lente e seriali lato server:
 *   - GET /tradebots                      (rapida, ma porta con sé i trade eager)
 *   - GET /accounts/get-all-balances      LENTA: N account × fetchBalance
 *   - GET /wallets/balances/summary       LENTA: fino a 25 wallet × 4 catene
 *   - GET /backtester/search?limit=5      rapida, è l'unico endpoint paginato
 *
 * Ognuna sta in una richiesta sua e disegna il suo blocco appena arriva: chi
 * apre la schermata vede subito i bot invece di guardare uno spinner finché
 * l'ultimo exchange non ha risposto. Il totale del capitale compare solo quando
 * ha entrambe le fonti, e finché ne ha una sola lo dichiara parziale — un
 * totale che tace una fonte è peggio di nessun totale.
 *
 * La richiesta di `GET /dashboard/summary` è annotata in RICHIESTE-API.md.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avvertenza, Distintivo, Pulsante, Scheda } from '../componenti';
import { IntestazionePagina } from '../componenti/Guscio';
import { IconaStrategia } from '../componenti/IconaStrategia';
import { ListaRisultati, type RigaRisultato } from '../componenti/ListaRisultati';
import { TernaMetriche } from '../componenti/TernaMetriche';
import { ContenitoreDati, ScheletroLista } from '../componenti/Stati';
import { accounts, backtest, tradebots, wallets, CAMPI_LISTA_BOT } from '../api/risorse';
import { usaRichiesta } from '../api/usaRichiesta';
import { daQuanto, num, ora, periodo, valuta } from '../lib/formato';
import { calcolaVerdetto } from '../lib/metriche';
import { capitaleDi, ordinaPerImportanza, vitalitaDi, type StatoBattito } from '../lib/vitalita';
import { Tessera } from './dashboard/Tessera';
import {
  leggiSalute,
  operazioniDi,
  riassuntoFermiConDenaro,
  type Segnalazione,
} from './dashboard/salute';
import { periodoBot, sedeBot } from './tradebot/modello';
import type { Tradebot } from '../api/tipi';
import './dashboard/dashboard.css';

/* La proiezione è quella condivisa: `CAMPI_LISTA_BOT` porta già `errors`,
   `failedTransactions`, il battito (`lastSeenAt`, `lastSeenPrice`) e i saldi che
   servono a valutare il capitale. Qui c'era un elenco locale che aggiungeva
   `errors` e `failedTransactions` per conto suo — necessario finché la
   proiezione condivisa non li chiedeva — più `PID`, che questa schermata non
   legge da nessuna parte: una colonna in più su ogni riga di ogni caricamento. */

const QUANTI_FERMI_IN_VISTA = 4;

export function Dashboard() {
  const naviga = useNavigate();

  /* ---- 1. i bot: rapida, ed è quella che risponde a "va tutto bene?" ---- */
  const bot = usaRichiesta(() => tradebots.elenco(CAMPI_LISTA_BOT), []);
  const listaBot = useMemo<Tradebot[]>(() => bot.dati?.data ?? [], [bot.dati]);
  const salute = useMemo(() => leggiSalute(listaBot), [listaBot]);

  /* Chi lavora e chi no: due elenchi, non uno ordinato per data di creazione. */
  const inMarcia = useMemo(
    () => ordinaPerImportanza(listaBot.filter((b) => b.isRunning)),
    [listaBot],
  );
  const fermi = useMemo(
    () => ordinaPerImportanza(listaBot.filter((b) => !b.isRunning)),
    [listaBot],
  );
  const rigaFermi = useMemo(() => riassuntoFermiConDenaro(salute), [salute]);

  /* ---- 2. capitale sugli exchange: LENTA, con il suo stato di attesa ---- */
  const saldiCex = usaRichiesta((segnale) => accounts.saldiTutti(segnale), []);

  /* ---- 3. capitale sui wallet: prima dalla cache del server, poi a richiesta
       dalle catene. `cacheOnly` non fa partire nessuna scansione, quindi la
       prima schermata non aspetta quattro RPC per wallet. ---- */
  const [leggiCatene, setLeggiCatene] = useState(0);
  const saldiWallet = usaRichiesta(
    (segnale) =>
      wallets.riepilogoSaldi(
        leggiCatene > 0 ? { refresh: true } : { cacheOnly: true },
        segnale,
      ),
    [leggiCatene],
  );

  /* ---- 4. ultimi backtest: leggera, è l'unico endpoint paginato ---- */
  const ultimi = usaRichiesta(
    (segnale) => backtest.cerca({ limit: 5, sortBy: 'createdAt', sortDir: 'DESC' }, segnale),
    [],
  );

  const totaleCex = saldiCex.dati?.total ?? null;
  const risposteWallet = saldiWallet.dati;
  /* Il filtro è un predicato di tipo: senza, il `reduce` qui sotto avrebbe
     avuto bisogno di `w.totalUsd ?? 0`, cioè di un ripiego a zero su un saldo
     in dollari — inerte oggi, ma è una cifra di denaro e non deve esistere. */
  const wLetti = (risposteWallet ?? []).filter(
    (w): w is (typeof w) & { totalUsd: number } => w.totalUsd != null,
  );
  const wSenzaLettura = (risposteWallet ?? []).length - wLetti.length;
  /* Nessun wallet registrato è zero, non "sconosciuto": distinguere i due casi
     è la differenza fra un totale onesto e un totale che resta per sempre
     "parziale" perché aspetta una fonte che non esiste. */
  const totaleWallet =
    risposteWallet == null
      ? null
      : risposteWallet.length === 0
        ? 0
        : wLetti.length > 0
          ? wLetti.reduce((s, w) => s + w.totalUsd, 0)
          : null;

  const totale = totaleCex != null && totaleWallet != null ? totaleCex + totaleWallet : null;
  /* La scomposizione si costruisce QUI, dove i due addendi sono ancora
     verificati. Nel JSX servivano due `?? 0`: oggi irraggiungibili, ma il
     giorno in cui la condizione del totale cambiasse scriverebbero «$0,00»
     per una fonte semplicemente non letta. */
  const dettaglioFonti =
    totaleCex != null && totaleWallet != null
      ? `exchange ${valuta(totaleCex, '$', 2)} + wallet ${valuta(totaleWallet, '$', 2)} — letture di momenti diversi`
      : null;
  const fontiMancanti = [
    totaleCex == null ? 'exchange' : null,
    totaleWallet == null ? 'wallet' : null,
  ].filter(Boolean) as string[];

  const contiInErrore = (saldiCex.dati?.balances ?? []).filter((b) => b.error);

  const quante = salute.segnalazioni.length;
  /* Un bot avviato di cui non si sa ancora il battito NON è un bot in avaria:
     `vitalitaDi` lo dice a chiare lettere (`allarme: false`), e la tessera non
     deve alzare la voce per una misura che manca. Qui contano solo quelli per
     cui l'allarme è vero. */
  const nonRespirano = salute.segnalazioni.filter((s) => s.motivo === 'battito-fermo').length;

  return (
    <div className="sb-cornice">
      <IntestazionePagina
        titolo="Dashboard"
        sotto={
          <span className="sb-nota">
            quattro letture indipendenti · ognuna porta la sua ora, perché arrivano in momenti diversi
          </span>
        }
      />

      <div style={{ padding: 'var(--sb-pad-pagina)' }} className="sb-colonna">
        {/* ================= va tutto bene? ================= */}
        <div className="sb-d-kpi">
          {/* Il numero conta SOLO cose azionabili. Contava anche i bot fermi con
              denaro reale — nove non-problemi noti — e un contatore che si
              alza per cose che nessuno andrà a guardare smette di essere letto
              proprio il giorno in cui si alza per una cosa vera. */}
          <Tessera
            etichetta="Da guardare"
            stato={bot.stato === 'caricamento' ? 'caricamento' : 'pieno'}
            tono={quante > 0 ? 'cautela' : 'neutro'}
            valore={quante === 0 ? 'niente' : num(quante)}
            verdetto={
              quante === 0
                ? 'Niente che chieda una decisione adesso'
                : quante === 1
                  ? '1 cosa da guardare adesso'
                  : `${quante} cose da guardare adesso`
            }
            qualificatore={
              salute.fermiConDenaroReale.length > 0
                ? `errori, transazioni fallite e bot in marcia che non respirano · i ${salute.fermiConDenaroReale.length} fermi con denaro reale non contano: sono stati spenti apposta`
                : 'errori, transazioni fallite e bot in marcia che non respirano'
            }
          />

          <Tessera
            etichetta="Bot in marcia"
            stato={bot.stato === 'caricamento' ? 'caricamento' : 'pieno'}
            tono={nonRespirano > 0 ? 'cautela' : 'neutro'}
            valore={num(salute.attivi)}
            verdetto={
              salute.attivi === 0
                ? 'Nessun bot sta operando'
                : nonRespirano > 0
                  ? nonRespirano === 1
                    ? '1 non dà segni di vita'
                    : `${nonRespirano} non danno segni di vita`
                  : salute.vivi === salute.attivi
                    ? salute.attivi === 1
                      ? 'in marcia, e dà segni di vita'
                      : `tutti e ${salute.attivi} danno segni di vita`
                    : 'in marcia; il battito non è ancora stato scritto'
            }
            qualificatore={`su ${listaBot.length} registrati · ${salute.fermi} fermi · letti ${bot.letturaA ? daQuanto(bot.letturaA) : '—'}`}
          />

          <Tessera
            etichetta="Capitale sugli exchange"
            stato={saldiCex.stato === 'caricamento' ? 'caricamento' : 'pieno'}
            valore={totaleCex == null ? 'n/d' : valuta(totaleCex, '$', 2)}
            verdetto={
              saldiCex.stato === 'errore'
                ? 'Lettura non riuscita'
                : contiInErrore.length > 0
                  ? `${contiInErrore.length} conti non hanno risposto`
                  : `${(saldiCex.dati?.balances ?? []).length} conti letti`
            }
            qualificatore={
              saldiCex.letturaA
                ? `letto alle ${ora(saldiCex.letturaA)} · comprende le polveri sotto 1 $`
                : 'lettura seriale: un conto per volta'
            }
            tono={saldiCex.stato === 'errore' || contiInErrore.length > 0 ? 'cautela' : 'neutro'}
          />

          <Tessera
            etichetta="Capitale sui wallet"
            stato={saldiWallet.stato === 'caricamento' ? 'caricamento' : 'pieno'}
            valore={totaleWallet == null ? 'n/d' : valuta(totaleWallet, '$', 2)}
            verdetto={
              risposteWallet != null && risposteWallet.length === 0
                ? 'Nessun wallet registrato'
                : wSenzaLettura > 0
                  ? `${wSenzaLettura} wallet non ancora letti dalle catene`
                  : `${wLetti.length} wallet letti`
            }
            qualificatore={
              leggiCatene > 0
                ? `letto dalle catene alle ${saldiWallet.letturaA ? ora(saldiWallet.letturaA) : '—'}`
                : 'dalla cache del server: nessuna scansione avviata'
            }
            tono={wSenzaLettura > 0 ? 'cautela' : 'neutro'}
            azione={
              <Pulsante
                variante="tenue"
                onClick={() => setLeggiCatene((n) => n + 1)}
                disabled={saldiWallet.inAggiornamento || saldiWallet.stato === 'caricamento'}
              >
                {saldiWallet.inAggiornamento || saldiWallet.stato === 'caricamento'
                  ? 'Scansione in corso…'
                  : 'Leggi dalle catene'}
              </Pulsante>
            }
          />
        </div>

        {/* ================= chi sta lavorando adesso =================
            Il primo blocco sotto le tessere. Prima erano le nove schede dei bot
            fermi, e questa riga stava in fondo alla pagina. */}
        <div>
          <div className="sb-d-titolo-sezione">
            <span className="sb-d-titolo-sezione__testo">In marcia adesso</span>
            <Pulsante variante="nudo" onClick={() => naviga('/tradebot')}>
              Vedi tutti i bot
            </Pulsante>
          </div>

          <ContenitoreDati
            stato={bot.stato}
            causaVuoto="Non c'è nessun tradebot registrato: la lista si popola creando un bot dalla schermata Tradebot."
            azioneVuoto={{ etichetta: 'Vai ai tradebot', onClick: () => naviga('/tradebot') }}
            messaggioErrore={bot.errore?.message}
            onRiprova={bot.ricarica}
            scheletro={<ScheletroLista righe={2} />}
          >
            {inMarcia.length === 0 ? (
              <Avvertenza livello="nota">
                Nessuno dei {listaBot.length} bot registrati sta operando: nessun processo è in
                ascolto, quindi qui non c'è niente che si muova da solo. I numeri dei bot fermi
                sono quelli dell'ultima volta che hanno scritto.
              </Avvertenza>
            ) : (
              <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
                {inMarcia.map((b) => (
                  <RigaBot key={b.id} bot={b} onApri={() => naviga(`/tradebot/${b.id}`)} />
                ))}
              </div>
            )}

            {/* La spiegazione che prima stava sotto OGNI riga, due righe per bot
                e sempre la stessa. Detta una volta, sotto l'elenco a cui si
                applica, occupa una riga invece di ventiquattro. */}
            {inMarcia.length > 0 && (
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                Il drawdown non è una colonna del motore dei tradebot: la casella resta n/d per
                tutti. Il capitale è valutato al prezzo più recente che il bot ha visto; dove la
                fonte del prezzo cambia quanto ci si può fidare, la riga lo dice.
              </div>
            )}
          </ContenitoreDati>
        </div>

        {/* ================= cosa chiede una decisione =================
            Solo cose azionabili. Se non ce n'è, non c'è nemmeno la scheda. */}
        {quante > 0 && (
          <Scheda tono="cautela">
            <div className="sb-d-titolo-sezione">
              <span className="sb-d-titolo-sezione__testo">Da guardare adesso</span>
              <span className="sb-nota">in ordine di gravità</span>
            </div>
            <div className="sb-colonna" style={{ gap: 'var(--sb-s-3)' }}>
              {salute.segnalazioni.map((s) => (
                <RigaSegnalazione key={s.bot.id} segnalazione={s} onApri={() => naviga(`/tradebot/${s.bot.id}`)} />
              ))}
            </div>
          </Scheda>
        )}

        {/* ================= quanto vale =================
            Il totale non compare finché non ha tutte e due le fonti: un totale
            che tace una fonte è peggio di nessun totale. */}
        <Scheda>
          <div className="sb-riga">
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="sb-etichetta">Patrimonio complessivo</div>
              <div className="sb-d-tessera__valore" style={{ marginTop: 'var(--sb-s-4)', fontSize: 'var(--sb-t-26)' }}>
                {totale == null ? 'parziale' : valuta(totale, '$', 2)}
              </div>
              <div className="sb-d-tessera__qualificatore" style={{ marginTop: 'var(--sb-s-3)' }}>
                {totale == null
                  ? `manca ancora ${fontiMancanti.join(' e ')}: finché manca una fonte non c'è un totale`
                  : dettaglioFonti}
              </div>
            </div>
          </div>

          {contiInErrore.length > 0 && (
            <div className="sb-colonna" style={{ marginTop: 'var(--sb-s-6)', gap: 'var(--sb-s-3)' }}>
              {contiInErrore.map((b) => (
                <Avvertenza key={b.account?.id ?? b.account?.name} livello="cautela">
                  {b.account?.name ?? 'conto'}: {b.error}
                </Avvertenza>
              ))}
            </div>
          )}

          {saldiCex.stato === 'errore' && (
            <div style={{ marginTop: 'var(--sb-s-6)' }}>
              <Avvertenza livello="cautela">
                I saldi degli exchange non sono arrivati: {saldiCex.errore?.message}. La lettura è
                seriale e senza timeout complessivo, quindi con molti conti può scadere prima di
                finire.
              </Avvertenza>
            </div>
          )}

          {/* La serie storica non è leggibile: `historical-balance` esiste nel
              database ma non ha né controller né service, quindi non c'è rotta
              HTTP che la esponga. Era una scheda intera con un'avvertenza in
              terza posizione nella pagina: è un limite permanente e noto, e un
              limite noto non merita più di una riga. RICHIESTE-API.md, punto 1. */}
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
            Patrimonio nel tempo: non disegnabile. La serie storica non ha una rotta HTTP che la
            esponga, e ricostruirla dai trade sarebbe un grafico inventato (RICHIESTE-API.md, 1).
          </div>
        </Scheda>

        {/* ================= i bot fermi e gli ultimi risultati ================= */}
        <div className="sb-d-colonne">
          <div>
            <div className="sb-d-titolo-sezione">
              <span className="sb-d-titolo-sezione__testo">Bot fermi</span>
              <Pulsante variante="nudo" onClick={() => naviga('/tradebot')}>
                Vedi tutti
              </Pulsante>
            </div>

            {/* La riga sola che ha preso il posto delle nove schede identiche. */}
            {rigaFermi && (
              <div className="sb-nota" style={{ marginBottom: 'var(--sb-s-4)' }}>
                {rigaFermi}
              </div>
            )}

            {fermi.length === 0 ? (
              <div className="sb-nota">Nessun bot fermo.</div>
            ) : (
              <div className="sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
                {fermi.slice(0, QUANTI_FERMI_IN_VISTA).map((b) => (
                  <RigaBot key={b.id} bot={b} onApri={() => naviga(`/tradebot/${b.id}`)} />
                ))}
                {fermi.length > QUANTI_FERMI_IN_VISTA && (
                  <span className="sb-nota">
                    e altri {fermi.length - QUANTI_FERMI_IN_VISTA}: l'elenco completo è in Tradebot
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="sb-d-titolo-sezione">
              <span className="sb-d-titolo-sezione__testo">Ultimi backtest</span>
              <Pulsante variante="nudo" onClick={() => naviga('/backtest')}>
                Vedi tutti
              </Pulsante>
            </div>

            <ListaRisultati
              righe={(ultimi.dati?.data ?? []).map(aRiga)}
              stato={ultimi.stato}
              causaVuoto="Nessun backtest è stato ancora eseguito: la ricerca non ha righe da ordinare."
              messaggioErrore={ultimi.errore?.message}
              onRiprova={ultimi.ricarica}
              onApri={(id) => naviga(`/backtest/${id}`)}
            />
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              i cinque più recenti per data di creazione, non i migliori
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Righe
 * ======================================================================== */

function RigaSegnalazione({
  segnalazione,
  onApri,
}: {
  segnalazione: Segnalazione;
  onApri: () => void;
}) {
  const b = segnalazione.bot;
  return (
    <button type="button" className="sb-d-bot sb-d-bot--attenzione" onClick={onApri}>
      <div className="sb-d-bot__testata">
        <IconaStrategia nome={b.strategyName} dim={22} />
        <span className="sb-d-bot__coppia">
          {b.asset}/{b.currency}
        </span>
        <Distintivo tono="cautela">
          {segnalazione.motivo === 'battito-fermo'
            ? 'in marcia, ma non respira'
            : segnalazione.motivo === 'errore'
              ? 'errore'
              : 'transazioni fallite'}
        </Distintivo>
        {b.useRealMoney && <Distintivo tono="cautela">denaro reale</Distintivo>}
        <span className="sb-spazio" />
        <span className="sb-nota">#{b.id}</span>
      </div>
      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        {segnalazione.testo}
      </div>
    </button>
  );
}

/** Le parole del battito, per chi guarda la riga. `vitalitaDi` ne dà una frase
 *  intera; qui serve l'etichetta corta del distintivo. */
function etichettaBattito(stato: StatoBattito): string {
  switch (stato) {
    case 'vivo':
      return 'in marcia';
    case 'non-misurabile':
      return 'in marcia';
    case 'in-ritardo':
      return 'battito in ritardo';
    case 'muto':
      return 'nessun segno di vita';
    default:
      return 'fermo';
  }
}

function classePunto(stato: StatoBattito): string {
  if (stato === 'in-ritardo' || stato === 'muto') return 'sb-d-punto--attenzione';
  if (stato === 'fermo') return '';
  return 'sb-d-punto--vivo';
}

function RigaBot({ bot: b, onApri }: { bot: Tradebot; onApri: () => void }) {
  /* Il periodo passa da `periodoBot`, che sa quando la fine non c'è. Qui si
     faceva `b.endDate ?? new Date()`: un bot FERMO da mesi veniva presentato
     come misurato fino a oggi. Un periodo sbagliato è peggio di uno assente,
     perché non si vede che è sbagliato. */
  const p = periodoBot(b);
  const v = vitalitaDi(b);
  /* Il capitale non è più `n/d`: l'asset viene valutato al prezzo più recente
     che si conosce, e la fonte del prezzo viaggia col numero. Un bot con
     0,0254 BTCB mostra il suo controvalore, non un trattino. */
  const cap = capitaleDi(b);

  /* Una riga sola di note, e solo con ciò che è specifico di QUESTO bot: il
     resto — il drawdown che il motore non registra — è detto una volta sotto
     l'elenco. Prima ogni bot ripeteva la stessa frase su due righe. */
  const note = [
    v.stato === 'fermo' ? null : v.testo,
    cap.motivo,
    b.marketChange == null ? null : `mercato ${num(b.marketChange, 1)}%`,
  ].filter((s): s is string => s != null);

  return (
    <button
      type="button"
      className={`sb-d-bot${v.allarme ? ' sb-d-bot--attenzione' : ''}`}
      onClick={onApri}
    >
      <div className="sb-d-bot__testata">
        <IconaStrategia nome={b.strategyName} dim={22} />
        <span className={`sb-d-punto ${classePunto(v.stato)}`} aria-hidden="true" />
        <span className="sb-d-bot__coppia">
          {b.asset}/{b.currency}
        </span>
        {/* La parola accanto al pallino: il colore da solo non dice niente a
            chi non lo distingue, e su questa riga significa "sta operando". */}
        <Distintivo tono={v.allarme ? 'cautela' : 'neutro'}>{etichettaBattito(v.stato)}</Distintivo>
        {b.useRealMoney && <Distintivo tono="cautela">denaro reale</Distintivo>}
        <span className="sb-spazio" />
        <span className="sb-nota">
          #{b.id} · {b.strategyName}
        </span>
      </div>

      {/* Di chi sono i soldi: il wallet o il conto su cui il bot opera. Con piu' bot sulla
          stessa strategia e sulla stessa coppia, era l'unica cosa che li distingueva e non c'era. */}
      <div className="sb-d-bot__sede">{sedeBot(b)}</div>

      {/* Il capitale governato, nella valuta di quotazione del bot: è la stessa
          unità in cui il dettaglio lo scrive, e non è un dollaro travestito. */}
      <div className="sb-d-bot__capitale">
        <span className="sb-d-bot__cifra">
          {cap.valore == null ? 'n/d' : `${num(cap.valore, 0)} ${b.currency}`}
        </span>
        <span className="sb-d-bot__cifra-etichetta">capitale governato</span>
        {cap.valore != null && cap.parziale && <Distintivo tono="cautela">parziale</Distintivo>}
      </div>

      {/* Regola 1: il profitto non va mai da solo. Il drawdown di un tradebot
          non esiste come colonna — si mostra `n/d`, non si toglie la casella:
          togliere la casella farebbe leggere il profitto come completo. */}
      <div style={{ marginTop: 'var(--sb-s-4)' }}>
        <TernaMetriche
          profitto={b.strategyChange}
          drawdown={null}
          operazioni={operazioniDi(b)}
          periodo={p.etichetta}
          durata={p.durata}
          densita="compatta"
        />
      </div>

      {note.length > 0 && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
          {note.join(' · ')}
        </div>
      )}
    </button>
  );
}

/** Da riga di ricerca a riga di lista. Il distintivo è il verdetto: il
 *  rendimento senza il confronto col mercato non dice se la strategia serviva. */
function aRiga(r: {
  id: number;
  strategyName: string;
  asset: string;
  currency: string;
  candleSize: number;
  strategyChange: number | null;
  marketChange: number | null;
  drawdownMaxValue: number | null;
  tradesNumber: number;
  startDate: string;
  endDate: string;
  calmar: number | null;
  sharpe: number | null;
}): RigaRisultato {
  const v =
    r.strategyChange != null && r.marketChange != null
      ? calcolaVerdetto(r.strategyChange, r.marketChange)
      : null;

  return {
    id: r.id,
    strategia: r.strategyName,
    asset: r.asset,
    currency: r.currency,
    candleSize: r.candleSize,
    profitto: r.strategyChange,
    drawdown: r.drawdownMaxValue,
    operazioni: r.tradesNumber,
    periodo: periodo(r.startDate, r.endDate),
    distintivo: v ? { testo: v.etichetta, tono: v.tono } : undefined,
    calmar: r.calmar,
    sharpe: r.sharpe,
  };
}
